import type { NativeSession } from "../native/session";
import {
  blockConfig,
  blockRoots,
  blockTemplates,
  createBlock,
  isBlockPart,
  updateBlock,
  type BlockConfig,
  type BlockTemplateId,
} from "../native/blocks";
import { emptyNativeDocument } from "../native/document";
import { NativeCanvasRenderer } from "../render/native-canvas";
import {
  originalCompact,
  syncOriginalCompact,
} from "./native-original-controls";
import { NativeToolGroups } from "./native-tool-groups";
import { LibraryTap } from "./native-library-tap";
import "./native-blocks.css";

const el = <T extends HTMLElement>(id: string) =>
  document.getElementById(id) as T;
/** Block controls delegate every document change to the shared native block commands. */
export class NativeBlocks {
  private dialog = document.createElement("dialog");
  private grid = document.createElement("div");
  private selected: BlockTemplateId | undefined;
  private taps: LibraryTap;
  private use = document.createElement("button");
  private preview = false;
  private fontToken = 0;
  private lastSelection = "";
  private compact: HTMLElement;
  constructor(
    private session: NativeSession,
    private changed: () => void,
    private prepare: () => boolean,
    private center: () => { x: number; y: number },
    private selectTool: () => void,
    private message: (text: string, error?: boolean) => void,
    openImages: () => void,
  ) {
    this.dialog.id = "native-block-library";
    this.dialog.className = "native-block-library";
    this.dialog.setAttribute("aria-labelledby", "native-block-library-title");
    this.dialog.innerHTML =
      '<header><div><h2 id="native-block-library-title">Библиотека блоков</h2><p>Готовые рамки с номером и заголовком</p></div><button type="button" class="block-close" aria-label="Закрыть">×</button></header>';
    this.grid.className = "native-block-grid";
    this.taps = new LibraryTap(this.grid);
    for (const t of blockTemplates) {
      const tile = document.createElement("button");
      tile.type = "button";
      tile.className = "clipart-tile block-card";
      tile.dataset.file = t.id;
      tile.dataset.blockTemplate = t.id;
      tile.setAttribute("aria-pressed", "false");
      const canvas = document.createElement("canvas"),
        renderer = new NativeCanvasRenderer(canvas, { get: () => undefined });
      const doc = emptyNativeDocument();
      doc.pages[0]!.nodes = createBlock(t.id, 240, 140, () =>
        crypto.randomUUID(),
      );
      renderer.draw(
        {
          document: doc,
          camera: { x: 7, y: 7, zoom: 0.6 },
          width: 302,
          height: 182,
          dpr: 2,
        },
        true,
      );
      const image = document.createElement("img");
      image.src = canvas.toDataURL();
      image.alt = "";
      renderer.dispose();
      const title = document.createElement("span");
      title.textContent = t.name;
      tile.append(image, title);
      tile.onclick = (event) => {
        const tap = this.taps.click(tile, event);
        if (tap === "ignore") return;
        this.selected = t.id;
        this.use.disabled = false;
        this.grid
          .querySelectorAll("button")
          .forEach((b) => b.setAttribute("aria-pressed", String(b === tile)));
        if (tap === "double") this.insert();
      };
      tile.ondblclick = () => {
        if (!this.taps.fromTouch) this.insert();
      };
      this.grid.append(tile);
    }
    const footer = document.createElement("footer"),
      cancel = document.createElement("button");
    cancel.type = "button";
    cancel.textContent = "Отмена";
    cancel.onclick = () => this.dialog.close();
    this.use.type = "button";
    this.use.id = "native-block-insert";
    this.use.textContent = "Добавить блок";
    this.use.disabled = true;
    this.use.onclick = () => this.insert();
    footer.append(cancel, this.use);
    this.dialog.append(this.grid, footer);
    el("native-editor").append(this.dialog);
    this.dialog.querySelector<HTMLButtonElement>(".block-close")!.onclick =
      () => this.dialog.close();
    this.dialog.addEventListener("close", () => {
      this.taps.reset();
      el("native-overlay").focus();
    });
    new NativeToolGroups<"images" | "blocks">(
      (tool) => (tool === "images" ? openImages() : this.open()),
      [el("native-image-group")],
      { "native-image-choice": "images", "native-block-choice": "blocks" },
    );
    const panel = document.createElement("section");
    panel.id = "native-block-properties";
    panel.className = "native-properties native-block-properties";
    panel.hidden = true;
    panel.innerHTML =
      '<h3>Блок</h3><label>Цветовой тон <button id="native-block-color-open" type="button" data-color-input="native-block-color" aria-label="Цветовой тон блока"></button><input id="native-block-color" hidden type="color" value="#92633e" data-display-alpha="1" data-opaque-only="true"></label><label>Номер <input id="native-block-number" type="text" maxlength="4"></label><label>Заголовок <input id="native-block-title" type="text" maxlength="200"></label><label>Шрифт <select id="native-block-font"></select></label><label>Размер текста, px <input id="native-block-size" type="number" min="12" max="60" step="1"></label><input id="native-block-size-range" type="range" min="12" max="60" step="1" aria-label="Размер текста блока"><p>При растягивании блока размер номера и текста сохраняется.</p>';
    document
      .querySelector('[data-property-section="dimensions"]')!
      .after(panel);
    el<HTMLSelectElement>("native-block-font").innerHTML =
      el("font-family").innerHTML;
    const fields = {
      color: "color",
      number: "number",
      title: "title",
      font: "fontFamily",
      size: "fontSize",
    } as const;
    for (const [id, key] of Object.entries(fields)) {
      const input = el<HTMLInputElement>("native-block-" + id);
      input.oninput = () => {
        if (key !== "fontFamily")
          this.apply(
            { [key]: key === "fontSize" ? Number(input.value) : input.value },
            false,
          );
      };
      input.onchange = () => {
        if (key === "fontFamily") void this.font(input.value);
        else
          this.apply(
            { [key]: key === "fontSize" ? Number(input.value) : input.value },
            true,
          );
      };
      input.onkeydown = (e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          if (this.preview) this.session.cancel();
          this.preview = false;
          this.render(true);
          this.changed();
        }
      };
    }
    for (const name of ["input", "change"]) {
      el("native-block-size-range").addEventListener(name, () => {
        const value = el<HTMLInputElement>("native-block-size-range").value;
        el<HTMLInputElement>("native-block-size").value = value;
        this.apply({ fontSize: Number(value) }, name === "change");
      });
    }
    this.compact = originalCompact("native-block-compact", [
      { id: "native-block-color", icon: "fill", title: "Цветовой тон блока" },
      { id: "native-block-number", icon: "font-size", title: "Номер задания" },
      { id: "native-block-title", icon: "font", title: "Заголовок блока" },
      { id: "native-block-font", icon: "font", title: "Шрифт блока" },
      {
        id: "native-block-size",
        icon: "font-size",
        title: "Размер текста блока",
      },
    ]);
    document.addEventListener(
      "pointerdown",
      (e) => {
        if (
          this.preview &&
          !(e.target as Element).closest(
            "#native-block-properties,#native-block-compact,#irl-color-picker",
          )
        ) {
          session.finish();
          this.preview = false;
        }
      },
      true,
    );
  }
  get active() {
    return this.preview;
  }
  private open() {
    if (!this.prepare()) return;
    this.taps.reset();
    this.selected = undefined;
    this.use.disabled = true;
    this.grid
      .querySelectorAll("button")
      .forEach((b) => b.setAttribute("aria-pressed", "false"));
    this.dialog.showModal();
  }
  private insert() {
    if (!this.dialog.open || !this.selected) return;
    const p = this.center(),
      nodes = createBlock(this.selected, p.x, p.y, () => crypto.randomUUID());
    this.session.replaceNodes([...this.session.page.nodes, ...nodes]);
    this.session.select(nodes.map((n) => n.id));
    this.session.finish();
    this.dialog.close();
    this.selectTool();
    this.changed();
    this.message("Блок добавлен.");
  }
  private apply(patch: Partial<BlockConfig>, commit: boolean) {
    const roots = blockRoots(this.session.selectedNodes);
    if (!roots.length) return;
    if (this.session.gesturing && !this.preview) return;
    if (
      patch.fontSize !== undefined &&
      (!Number.isFinite(patch.fontSize) ||
        patch.fontSize < 12 ||
        patch.fontSize > 60)
    ) {
      if (commit) this.message("Размер текста блока — от 12 до 60 px.", true);
      return;
    }
    this.fontToken++;
    if (!this.preview) {
      this.session.begin();
      this.preview = true;
    }
    try {
      let nodes = this.session.page.nodes;
      for (const root of roots)
        nodes = updateBlock(nodes, String(root.layer.groupId), patch);
      this.session.replaceNodes(nodes);
      if (commit) {
        this.session.finish();
        this.preview = false;
      }
      this.changed();
    } catch (error) {
      this.session.cancel();
      this.preview = false;
      this.message((error as Error).message, true);
      this.changed();
    }
  }
  private async font(family: string) {
    const token = ++this.fontToken,
      gen = this.session.generation,
      rev = this.session.revision,
      ids = this.session.selectedIds.join();
    try {
      await document.fonts.load(`bold 24px "${family.replace(/["\\]/g, "")}"`);
      if (
        token !== this.fontToken ||
        gen !== this.session.generation ||
        rev !== this.session.revision ||
        ids !== this.session.selectedIds.join()
      )
        return;
      this.apply({ fontFamily: family }, true);
    } catch {
      this.message("Не удалось загрузить шрифт.", true);
    }
  }
  render(force = false) {
    if (!this.session.gesturing) this.preview = false;
    const roots = blockRoots(this.session.selectedNodes),
      has = roots.length > 0;
    el("native-editor").dataset.blockSelection = String(has);
    el("native-block-properties").hidden = this.compact.hidden = !has;
    const key = this.session.generation + ":" + this.session.selectedIds.join();
    if (key !== this.lastSelection) {
      this.lastSelection = key;
      this.compact
        .querySelectorAll<HTMLElement>(".native-property-popup")
        .forEach((p) => (p.hidden = true));
    }
    if (!has) return;
    const only = this.session.selectedNodes.every(isBlockPart);
    el("native-selection-title").textContent = only
      ? "Блок"
      : "Несколько объектов";
    if (only)
      el("native-selection-count").textContent =
        roots.length === 1
          ? "Выбран 1 блок"
          : `Выбрано блоков: ${roots.length}`;
    const c = blockConfig(roots[0]!)!;
    el("native-block-color-open").style.backgroundColor = c.color;
    for (const [id, value] of Object.entries({
      color: c.color,
      number: c.number,
      title: c.title,
      font: c.fontFamily,
      size: c.fontSize,
    })) {
      const input = el<HTMLInputElement>("native-block-" + id);
      input.disabled =
        (id === "title" || id === "number") && roots.length !== 1;
      if (force || document.activeElement !== input)
        input.value = input.disabled ? "" : String(value);
      if (force)
        document
          .querySelectorAll<HTMLInputElement>(
            `[data-original-source="${input.id}"]`,
          )
          .forEach((p) => (p.value = input.value));
    }
    el<HTMLInputElement>("native-block-size-range").value = String(c.fontSize);
    syncOriginalCompact("native-block-compact");
    el("native-compact-align").hidden =
      this.session.selectedNodes.filter((n) => !isBlockPart(n)).length +
        roots.length <
      2;
  }
}
